/**
 * Print & Export Redux Slice
 *
 * Manages state for:
 * - Print dialog visibility and options
 * - Image export dialog visibility and options
 * - Layout composer state
 * - Layout templates
 * - Export progress and errors
 *
 * **Validates: Requirements 36, 37, 38**
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type {
  PrintExportState,
  PrintOptions,
  ImageExportOptions,
  MapLayout,
  LayoutTemplate,
  LayoutElement,
  PageSize,
  Orientation,
  DPI,
  ImageFormat,
} from '../../types/print';
import { DEFAULT_PRINT_OPTIONS, DEFAULT_IMAGE_EXPORT_OPTIONS } from '../../types/print';

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: PrintExportState = {
  printDialogOpen: false,
  imageExportDialogOpen: false,
  layoutComposerOpen: false,
  printOptions: { ...DEFAULT_PRINT_OPTIONS },
  imageExportOptions: { ...DEFAULT_IMAGE_EXPORT_OPTIONS },
  currentLayout: null,
  layoutTemplates: [],
  isExporting: false,
  exportError: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetches layout templates from the backend.
 */
export const fetchLayoutTemplates = createAsyncThunk(
  'printExport/fetchLayoutTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/layout-templates', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }
      return (await response.json()) as LayoutTemplate[];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch layout templates'
      );
    }
  }
);

/**
 * Saves a layout template to the backend.
 */
export const saveLayoutTemplate = createAsyncThunk(
  'printExport/saveLayoutTemplate',
  async (template: { name: string; layout: MapLayout }, { rejectWithValue }) => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
      if (csrfMatch && csrfMatch[2]) {
        headers['X-CSRF-Token'] = csrfMatch[2];
      }

      const response = await fetch('/api/layout-templates', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(template),
      });
      if (!response.ok) {
        throw new Error(`Failed to save template: ${response.statusText}`);
      }
      return (await response.json()) as LayoutTemplate;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to save layout template'
      );
    }
  }
);

/**
 * Deletes a layout template from the backend.
 */
export const deleteLayoutTemplate = createAsyncThunk(
  'printExport/deleteLayoutTemplate',
  async (templateId: string, { rejectWithValue }) => {
    try {
      const headers: Record<string, string> = {};
      const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
      if (csrfMatch && csrfMatch[2]) {
        headers['X-CSRF-Token'] = csrfMatch[2];
      }

      const response = await fetch(`/api/layout-templates/${templateId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to delete template: ${response.statusText}`);
      }
      return templateId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete layout template'
      );
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const printExportSlice = createSlice({
  name: 'printExport',
  initialState,
  reducers: {
    // Dialog visibility
    openPrintDialog(state) {
      state.printDialogOpen = true;
      state.exportError = null;
    },
    closePrintDialog(state) {
      state.printDialogOpen = false;
    },
    openImageExportDialog(state) {
      state.imageExportDialogOpen = true;
      state.exportError = null;
    },
    closeImageExportDialog(state) {
      state.imageExportDialogOpen = false;
    },
    openLayoutComposer(state) {
      state.layoutComposerOpen = true;
      state.exportError = null;
    },
    closeLayoutComposer(state) {
      state.layoutComposerOpen = false;
    },

    // Print options
    setPrintOptions(state, action: PayloadAction<Partial<PrintOptions>>) {
      state.printOptions = { ...state.printOptions, ...action.payload };
    },
    setPageSize(state, action: PayloadAction<PageSize>) {
      state.printOptions.pageSize = action.payload;
    },
    setOrientation(state, action: PayloadAction<Orientation>) {
      state.printOptions.orientation = action.payload;
    },
    setPrintDPI(state, action: PayloadAction<DPI>) {
      state.printOptions.dpi = action.payload;
    },
    setPrintTitle(state, action: PayloadAction<string>) {
      state.printOptions.title = action.payload;
    },
    toggleIncludeTitle(state) {
      state.printOptions.includeTitle = !state.printOptions.includeTitle;
    },
    toggleIncludeLegend(state) {
      state.printOptions.includeLegend = !state.printOptions.includeLegend;
    },
    toggleIncludeScaleBar(state) {
      state.printOptions.includeScaleBar = !state.printOptions.includeScaleBar;
    },
    toggleIncludeNorthArrow(state) {
      state.printOptions.includeNorthArrow = !state.printOptions.includeNorthArrow;
    },

    // Image export options
    setImageExportOptions(state, action: PayloadAction<Partial<ImageExportOptions>>) {
      state.imageExportOptions = { ...state.imageExportOptions, ...action.payload };
    },
    setImageFormat(state, action: PayloadAction<ImageFormat>) {
      state.imageExportOptions.format = action.payload;
    },
    setImageDimensions(state, action: PayloadAction<{ width: number; height: number }>) {
      state.imageExportOptions.width = action.payload.width;
      state.imageExportOptions.height = action.payload.height;
    },
    setImageDPI(state, action: PayloadAction<DPI>) {
      state.imageExportOptions.dpi = action.payload;
    },
    togglePreserveTransparency(state) {
      state.imageExportOptions.preserveTransparency =
        !state.imageExportOptions.preserveTransparency;
    },

    // Layout composer
    setCurrentLayout(state, action: PayloadAction<MapLayout | null>) {
      state.currentLayout = action.payload;
    },
    updateLayoutElement(
      state,
      action: PayloadAction<{ elementKey: string; updates: Partial<LayoutElement> }>
    ) {
      if (!state.currentLayout) return;
      const { elementKey, updates } = action.payload;

      if (elementKey === 'mapFrame') {
        state.currentLayout.mapFrame = { ...state.currentLayout.mapFrame, ...updates };
      } else if (elementKey === 'title' && state.currentLayout.title) {
        state.currentLayout.title = { ...state.currentLayout.title, ...updates };
      } else if (elementKey === 'legend' && state.currentLayout.legend) {
        state.currentLayout.legend = { ...state.currentLayout.legend, ...updates };
      } else if (elementKey === 'scaleBar' && state.currentLayout.scaleBar) {
        state.currentLayout.scaleBar = { ...state.currentLayout.scaleBar, ...updates };
      } else if (elementKey === 'northArrow' && state.currentLayout.northArrow) {
        state.currentLayout.northArrow = { ...state.currentLayout.northArrow, ...updates };
      } else if (elementKey.startsWith('textBox-')) {
        const index = parseInt(elementKey.replace('textBox-', ''), 10);
        if (state.currentLayout.textBoxes[index]) {
          state.currentLayout.textBoxes[index] = {
            ...state.currentLayout.textBoxes[index],
            ...updates,
          };
        }
      }
    },
    addLayoutElement(state, action: PayloadAction<LayoutElement>) {
      if (!state.currentLayout) return;
      const element = action.payload;

      switch (element.type) {
        case 'title':
          state.currentLayout.title = element;
          break;
        case 'legend':
          state.currentLayout.legend = element;
          break;
        case 'scale-bar':
          state.currentLayout.scaleBar = element;
          break;
        case 'north-arrow':
          state.currentLayout.northArrow = element;
          break;
        case 'text':
          state.currentLayout.textBoxes.push(element);
          break;
      }
    },
    removeLayoutElement(state, action: PayloadAction<string>) {
      if (!state.currentLayout) return;
      const elementKey = action.payload;

      if (elementKey === 'title') {
        state.currentLayout.title = undefined;
      } else if (elementKey === 'legend') {
        state.currentLayout.legend = undefined;
      } else if (elementKey === 'scaleBar') {
        state.currentLayout.scaleBar = undefined;
      } else if (elementKey === 'northArrow') {
        state.currentLayout.northArrow = undefined;
      } else if (elementKey.startsWith('textBox-')) {
        const index = parseInt(elementKey.replace('textBox-', ''), 10);
        state.currentLayout.textBoxes.splice(index, 1);
      }
    },

    // Export state
    setExporting(state, action: PayloadAction<boolean>) {
      state.isExporting = action.payload;
    },
    setExportError(state, action: PayloadAction<string | null>) {
      state.exportError = action.payload;
    },
    clearExportError(state) {
      state.exportError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchLayoutTemplates.fulfilled, (state, action) => {
        state.layoutTemplates = action.payload;
      })
      .addCase(fetchLayoutTemplates.rejected, (state, action) => {
        state.exportError = action.payload as string;
      })
      // Save template
      .addCase(saveLayoutTemplate.fulfilled, (state, action) => {
        state.layoutTemplates.push(action.payload);
      })
      .addCase(saveLayoutTemplate.rejected, (state, action) => {
        state.exportError = action.payload as string;
      })
      // Delete template
      .addCase(deleteLayoutTemplate.fulfilled, (state, action) => {
        state.layoutTemplates = state.layoutTemplates.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteLayoutTemplate.rejected, (state, action) => {
        state.exportError = action.payload as string;
      });
  },
});

export const {
  openPrintDialog,
  closePrintDialog,
  openImageExportDialog,
  closeImageExportDialog,
  openLayoutComposer,
  closeLayoutComposer,
  setPrintOptions,
  setPageSize,
  setOrientation,
  setPrintDPI,
  setPrintTitle,
  toggleIncludeTitle,
  toggleIncludeLegend,
  toggleIncludeScaleBar,
  toggleIncludeNorthArrow,
  setImageExportOptions,
  setImageFormat,
  setImageDimensions,
  setImageDPI,
  togglePreserveTransparency,
  setCurrentLayout,
  updateLayoutElement,
  addLayoutElement,
  removeLayoutElement,
  setExporting,
  setExportError,
  clearExportError,
} = printExportSlice.actions;

export default printExportSlice.reducer;
